import React, { useState, useEffect } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/add-blog.css";
import { useNavigate, useParams } from "react-router-dom";

// DnD Kit Imports
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Icons
import { GripVertical, Trash2, Image as ImageIcon, Type, FileText, Columns, Eye, X, ArrowLeft, ArrowRight, Zap } from "lucide-react";

// --- 1. Component Toolbar ---
const BlockToolbar = ({ onAdd, index }) => {
  return (
    <div className="block-toolbar" style={{ display: 'flex', gap: '10px', padding: '10px', background: '#f8f9fa', border: '1px dashed #dee2e6', borderRadius: '6px', margin: '15px 0', justifyContent: 'center', alignItems: 'center' }}>
      <span style={{ fontSize: '0.8rem', color: '#999', marginRight: 5 }}>Thêm tiếp:</span>
      <button type="button" className="btn-toolbar-item" onClick={() => onAdd(index, 'title')} style={toolbarBtnStyle}><Type size={16} /> Tiêu đề</button>
      <button type="button" className="btn-toolbar-item" onClick={() => onAdd(index, 'content')} style={toolbarBtnStyle}><FileText size={16} /> Nội dung</button>
      <button type="button" className="btn-toolbar-item" onClick={() => onAdd(index, 'image')} style={toolbarBtnStyle}><ImageIcon size={16} /> Ảnh</button>
      <button type="button" className="btn-toolbar-item" onClick={() => onAdd(index, 'split')} style={toolbarBtnStyle}><Columns size={16} /> 2 Cột</button>
    </div>
  );
};
const toolbarBtnStyle = { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', border: '1px solid #ced4da', background: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', color: '#495057' };

// --- 2. Component Block Item ---
const SortableBlock = ({ id, item, onChange, onRemove, handleImageChange, updateBlockMeta }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "20px", position: 'relative' };
  const simpleModules = { toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]] };

  return (
    <div ref={setNodeRef} style={style} className="sortable-item">
      <div {...attributes} {...listeners} className="drag-handle" style={{ position: 'absolute', right: 10, top: 10, cursor: 'grab', color: '#ccc', zIndex: 5 }}><GripVertical size={20} /></div>

      {item.type === "title" && (
        <div>
           <div style={{ display: "flex", gap: 10, marginBottom: 5 }}>
            <label className="form-label">Tiêu đề phân đoạn</label>
            <select className="form-select" style={{ width: "auto", padding: "2px 8px", height: 30 }} value={item.level || "h2"} onChange={(e) => updateBlockMeta(id, "level", e.target.value)}>
                <option value="h2">Mục lớn (H2)</option><option value="h3">Mục con (H3)</option>
            </select>
           </div>
          <input type="text" value={item.content} onChange={(e) => onChange(id, e.target.value)} className="form-input" placeholder="Nhập tiêu đề..." style={{fontWeight: 'bold', fontSize: '1.1rem'}} />
        </div>
      )}
      
      {item.type === "content" && (<div><label className="form-label">Nội dung văn bản</label><ReactQuill theme="snow" value={item.content} onChange={(val) => onChange(id, val)} modules={simpleModules} className="quill-editor" /></div>)}
      
      {item.type === "image" && (
        <div className="image-upload-wrapper">
          <label className="form-label">Hình ảnh</label>
          {/* Hiển thị ảnh (nếu có) */}
          {item.content && <img src={item.content} alt="Preview" style={{ maxWidth: '100%', marginBottom: 15, borderRadius: 8, display: 'block' }} />}
          {/* Nút đổi ảnh */}
          <input type="file" onChange={(e) => handleImageChange(id, e)} className="form-input" accept="image/*" />
          <small style={{color: '#666', marginTop: 5, display: 'block'}}>* Chọn tệp mới nếu bạn muốn thay đổi ảnh này.</small>
        </div>
      )}

      {item.type === "split" && (
        <div>
           <div className="split-controls" style={{marginBottom:10, paddingBottom:10, borderBottom:'1px solid #eee'}}>
              <label className="form-label" style={{margin:0, marginRight:10}}>Bố cục 2 cột:</label>
              <button type="button" className={`btn-sm`} onClick={() => updateBlockMeta(id, 'layout', 'left')} style={{marginRight:5, background: item.layout === 'left'?'#e0f2fe':'white', border:'1px solid #ccc', borderRadius:4, padding:'2px 8px'}}> <ArrowLeft size={14}/> Ảnh Trái </button>
              <button type="button" className={`btn-sm`} onClick={() => updateBlockMeta(id, 'layout', 'right')} style={{background: item.layout === 'right'?'#e0f2fe':'white', border:'1px solid #ccc', borderRadius:4, padding:'2px 8px'}}> Ảnh Phải <ArrowRight size={14}/> </button>
           </div>
           <div className={`split-layout ${item.layout === 'right' ? 'img-right' : ''}`} style={{display:'flex', gap:20, flexDirection: item.layout === 'right'?'row-reverse':'row'}}>
              <div className="split-col" style={{flex:1}}>
                <div style={{background:'#f3f4f6', padding:10, borderRadius:8, textAlign:'center'}}>
                  <ImageIcon size={24} style={{marginBottom:5, color:'#999'}}/>
                  {item.imageSrc && <img src={item.imageSrc} alt="Preview" style={{ width: '100%', height: '150px', objectFit: 'cover', marginBottom: 10, borderRadius: 4 }} />}
                  <input type="file" onChange={(e) => handleImageChange(id, e)} style={{fontSize:'0.8rem', width:'100%'}} accept="image/*" />
                </div>
              </div>
              <div className="split-col" style={{flex:2}}><ReactQuill theme="snow" value={item.content} onChange={(val) => onChange(id, val)} modules={simpleModules} placeholder="Mô tả ảnh..." style={{height:'150px'}} /></div>
           </div>
        </div>
      )}
      <button type="button" onClick={() => onRemove(id)} className="btn-icon" style={{ position: 'absolute', bottom: 10, right: 10, color: "#ef4444", border: 'none', background: 'transparent' }}><Trash2 size={18} /></button>
    </div>
  );
};

// --- 3. Main Component ---
const AddBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams(); 
  const isEditing = !!id; 

  const [formData, setFormData] = useState({ tieude: "", mota: "", danhmuc: "khac" });
  const [contentItems, setContentItems] = useState([]);
  const [imagesToUpload, setImagesToUpload] = useState({});
  const [message, setMessage] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  // --- EFFECT: Load dữ liệu nếu đang sửa ---
  useEffect(() => {
    if (isEditing) {
        const fetchBlog = async () => {
            try {
                const res = await axios.get(`https://dht.api.mekongzone.com.vn/api/tintuc/${id}`);
                const data = res.data;
                setFormData({ tieude: data.tieude || "", mota: data.mota || "", danhmuc: data.danhmuc || "khac" });
                
                // MẸO QUAN TRỌNG: Biến placeholder thành thẻ HTML để DOMParser không bị mất ảnh
                let processedHtml = data.noidung || "";
                processedHtml = processedHtml.replace(/{{IMAGE_PLACEHOLDER\|?(.*?)}}/g, '<figure class="img-ph" data-mode="$1"></figure>');

                // Parse HTML về Blocks
                const parser = new DOMParser();
                const doc = parser.parseFromString(processedHtml, 'text/html');
                const nodes = Array.from(doc.body.children);
                let newBlocks = [];
                let imgIndex = 0;
                let currentBuffer = "";

                const flushBuffer = () => {
                    if(currentBuffer) {
                        newBlocks.push({id: `block-${Date.now()}-${Math.random()}`, type: 'content', content: currentBuffer});
                        currentBuffer = "";
                    }
                }

                nodes.forEach(node => {
                    const tag = node.tagName.toLowerCase();
                    const className = node.className || "";

                    if(['h1','h2','h3','h4'].includes(tag)) {
                        flushBuffer();
                        newBlocks.push({id: `block-${Date.now()}-${Math.random()}`, type: 'title', content: node.innerText, level: tag === 'h3' ? 'h3' : 'h2'});
                    } 
                    // Bắt Block Ảnh Đơn
                    else if (tag === 'figure' && className.includes('img-ph')) {
                        flushBuffer();
                        const imgSrc = data.hinhanhs && data.hinhanhs[imgIndex] ? data.hinhanhs[imgIndex] : "";
                        imgIndex++;
                        newBlocks.push({
                            id: `block-${Date.now()}-${Math.random()}`, 
                            type: 'image', 
                            content: imgSrc,
                            displayMode: node.getAttribute('data-mode') || 'full'
                        });
                    } 
                    // Bắt Block 2 Cột (Split)
                    else if (className.includes('blog-split-row')) {
                        flushBuffer();
                        const imgSrc = data.hinhanhs && data.hinhanhs[imgIndex] ? data.hinhanhs[imgIndex] : "";
                        imgIndex++;
                        
                        const textCol = node.querySelector('.col-text');
                        const contentText = textCol ? textCol.innerHTML : "";
                        const isRight = className.includes('reverse');

                        newBlocks.push({
                            id: `block-${Date.now()}-${Math.random()}`,
                            type: 'split',
                            content: contentText,
                            imageSrc: imgSrc,
                            layout: isRight ? 'right' : 'left'
                        });
                    }
                    else {
                        currentBuffer += node.outerHTML;
                    }
                });
                flushBuffer();
                setContentItems(newBlocks);

            } catch (err) {
                setMessage("Lỗi tải bài viết cũ: " + err.message);
            }
        };
        fetchBlog();
    }
  }, [id, isEditing]);

  // --- Các hàm xử lý Block ---
  const addBlock = (index, type) => {
    const newItems = [...contentItems];
    newItems.splice(index + 1, 0, { id: `block-${Date.now()}`, type, content: "", level: 'h2', layout: 'left', imageSrc: "" });
    setContentItems(newItems);
  };

  const updateBlockContent = (id, value) => setContentItems(prev => prev.map(item => item.id === id ? { ...item, content: value } : item));
  const updateBlockMeta = (id, key, value) => setContentItems(prev => prev.map(item => item.id === id ? { ...item, [key]: value } : item));
  
  const handleImageChange = (id, e) => {
    const file = e.target.files[0];
    if (file) {
      setImagesToUpload(prev => ({ ...prev, [id]: file }));
      // Cập nhật URL tạm thời để hiển thị ra UI
      setContentItems(prev => prev.map(item => {
        if (item.id === id) {
             if(item.type === 'split') return { ...item, imageSrc: URL.createObjectURL(file) };
             return { ...item, content: URL.createObjectURL(file) };
        }
        return item;
      }));
    }
  };

  const removeBlock = (id) => {
      setContentItems(prev => prev.filter(item => item.id !== id));
      const newImages = {...imagesToUpload};
      delete newImages[id];
      setImagesToUpload(newImages);
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setContentItems(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const htmlContent = contentItems.map(item => {
      if (item.type === "title") return `<${item.level || 'h2'} class="blog-heading">${item.content}</${item.level || 'h2'}>`;
      if (item.type === "content") return `<div class="blog-text">${item.content}</div>`;
      if (item.type === "image") return `{{IMAGE_PLACEHOLDER|full}}`;
      if (item.type === "split") {
           const imgPart = `<div class="col-img">{{IMAGE_PLACEHOLDER|split}}</div>`;
           const textPart = `<div class="col-text">${item.content}</div>`;
           return `<div class="blog-split-row ${item.layout === 'right' ? 'reverse' : ''}" style="display:flex; gap:20px; align-items:center; margin: 20px 0;">${item.layout === 'left' ? imgPart + textPart : textPart + imgPart}</div>`;
      }
      return "";
    }).join("");

    const formDataToSend = new FormData();
    formDataToSend.append("tieude", formData.tieude);
    formDataToSend.append("mota", formData.mota);
    formDataToSend.append("danhmuc", formData.danhmuc);
    formDataToSend.append("noidung", htmlContent);
    
    // Gửi ảnh thay thế/mới
    contentItems.forEach(item => {
        if (imagesToUpload[item.id]) formDataToSend.append("hinhanhs", imagesToUpload[item.id]);
    });

    try {
      if (isEditing) {
          await axios.put(`https://dht.api.mekongzone.com.vn/api/tintuc/${id}`, formDataToSend, { headers: { "Content-Type": "multipart/form-data" } });
          setMessage("✅ Cập nhật bài viết thành công!");
      } else {
          await axios.post("https://dht.api.mekongzone.com.vn/api/tintuc", formDataToSend, { headers: { "Content-Type": "multipart/form-data" } });
          setMessage("✅ Đăng bài mới thành công!");
      }
      setTimeout(() => navigate('/admin/dashboard'), 1500);
    } catch (error) {
      setMessage(`❌ Lỗi: ${error.response?.data?.error || error.message}`);
    }
  };

  const renderPreview = () => {
    let html = contentItems.map(item => {
        if (item.type === "title") return `<${item.level} style="font-weight:bold; margin-top:20px">${item.content}</${item.level}>`;
        if (item.type === "content") return `<div>${item.content}</div>`;
        if (item.type === "image") return `<img src="${item.content}" style="max-width:100%; border-radius:8px;"/>`;
        if (item.type === "split") {
            const img = `<img src="${item.imageSrc}" style="width:100%; border-radius:8px;"/>`;
            const txt = `<div>${item.content}</div>`;
            return `<div style="display:flex; gap:20px; align-items:center;">${item.layout==='left' ? img+txt : txt+img}</div>`;
        }
        return "";
    }).join("");
    return html; 
  };

  return (
    <div className="blog-container">
      <div className="blog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><h1>{isEditing ? "Chỉnh Sửa Bài Viết" : "Viết Bài Mới"}</h1></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn-tool" onClick={() => navigate('/admin/dashboard')} style={{ background: '#6b7280', color: 'white' }}>Quay lại</button>
          <button type="button" className="btn-tool" onClick={() => setIsPreview(true)} style={{ background: '#2563eb', color: 'white' }}><Eye size={18} /> Xem trước</button>
        </div>
      </div>

      {message && <div className={`message ${message.includes("Lỗi") ? "error" : "success"}`}>{message}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <input type="text" value={formData.tieude} onChange={(e) => setFormData({ ...formData, tieude: e.target.value })} className="form-input" placeholder="Tiêu đề chính..." required style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: 15 }} />
          <textarea value={formData.mota} onChange={(e) => setFormData({ ...formData, mota: e.target.value })} className="form-textarea" placeholder="Sapo (Mô tả ngắn)..." required />
          <select value={formData.danhmuc} onChange={(e) => setFormData({ ...formData, danhmuc: e.target.value })} className="form-select" style={{ marginTop: 10 }}>
            <option value="khac">Khác</option><option value="bai-hoc">Bài học sô cô la</option><option value="ky-thuat">Kỹ thuật</option><option value="cong-thuc">Công thức</option>
          </select>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={contentItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div className="content-area">
              {contentItems.length === 0 && (<div style={{ textAlign: 'center', padding: 30 }}><p>Chưa có nội dung. Bắt đầu ngay:</p><BlockToolbar index={-1} onAdd={addBlock} /></div>)}
              {contentItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <SortableBlock id={item.id} item={item} onChange={updateBlockContent} updateBlockMeta={updateBlockMeta} onRemove={removeBlock} handleImageChange={handleImageChange} />
                  <BlockToolbar index={index} onAdd={addBlock} />
                </React.Fragment>
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <div className="submit-area" style={{ marginTop: 30, textAlign: 'right' }}>
          <button type="submit" className="btn-submit">{isEditing ? "Cập Nhật Bài Viết" : "Đăng Bài Viết"}</button>
        </div>
      </form>
      
      {/* Modal Preview */}
      {isPreview && (
        <div className="preview-modal" style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(255,255,255,0.95)', zIndex:2000, padding:20, overflow:'auto'}}>
            <div style={{maxWidth:800, margin:'0 auto', background:'white', padding:30, borderRadius:10, boxShadow:'0 5px 15px rgba(0,0,0,0.1)'}}>
                <button onClick={() => setIsPreview(false)} style={{float:'right', padding: '5px 15px', cursor: 'pointer'}}>Đóng</button>
                <h2>{formData.tieude}</h2>
                <div dangerouslySetInnerHTML={{__html: renderPreview()}}></div>
            </div>
        </div>
      )}
    </div>
  );
};

export default AddBlog;